/**
 * Footer Parallax Component
 *
 * Creates a parallax effect on the footer as it scrolls into view:
 * - Moves footer content vertically (yPercent)
 * - Fades in a dark overlay
 *
 * Uses GSAP and ScrollTrigger for smooth animations.
 * Works seamlessly with Lenis smooth scroll.
 */

import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

// Register GSAP plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Initialize footer parallax effect
 * Finds all elements with [data-footer-parallax] and applies the effect
 *
 * Performance optimized for mobile devices:
 * - Reduced parallax intensity on mobile
 * - Faster scrub value for smoother tracking
 * - GPU acceleration with force3D
 */
export function initFooterParallax() {
  const footerElements = document.querySelectorAll("[data-footer-parallax]");

  if (footerElements.length === 0) {
    return; // No footer parallax elements found
  }

  // Detect mobile devices
  const isMobile = window.matchMedia("(max-width: 768px)").matches;

  // Adjust parallax settings based on device
  const parallaxAmount = isMobile ? -25 : -55; // Less movement on mobile
  const scrubValue = isMobile ? 0.5 : true; // true = 0.5s (smooth), 0.5 = faster for mobile

  footerElements.forEach((el) => {
    const inner = el.querySelector("[data-footer-parallax-inner]");
    const dark = el.querySelector("[data-footer-parallax-dark]");

    // Create timeline with scroll trigger
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: "clamp(top bottom)", // When top of footer hits bottom of viewport
        end: "clamp(top top)", // When top of footer hits top of viewport
        scrub: scrubValue, // Faster scrub for smoother mobile performance
        // markers: true, // Uncomment for debugging
      },
    });

    // Animate footer inner content (parallax movement)
    if (inner) {
      tl.from(inner, {
        yPercent: parallaxAmount,
        ease: "linear",
        force3D: true, // Enable GPU acceleration
      });
    }

    // Animate dark overlay (fade in effect)
    if (dark) {
      tl.from(
        dark,
        {
          opacity: 0.5, // Fade from 50% to 100%
          ease: "linear",
          force3D: true, // Enable GPU acceleration
        },
        "<",
      ); // Start at the same time as the previous animation
    }
  });
}

/**
 * Cleanup function to destroy ScrollTrigger instances
 * Useful for SPA navigation or dynamic content updates
 */
export function destroyFooterParallax() {
  ScrollTrigger.getAll().forEach((trigger) => {
    if (trigger.vars.trigger?.hasAttribute("data-footer-parallax")) {
      trigger.kill();
    }
  });
}

export const footerParallax = {
  init: initFooterParallax,
  destroy: destroyFooterParallax,
};
